/* Portfolio Manager score matrix — schools x diagnostic pillars, color-coded by
   score band. Each school shows TWO explicitly-labeled rows grouped beneath its
   name: the School Assessment (school-owned diagnostic) and the Portfolio
   Assessment (portfolio-owned diagnostic). The two are distinct assessments —
   neither borrows the other's score. A cell shows a score only when that
   assessment is FINAL (approved); otherwise it shows the true status. A gap
   badge appears on the Portfolio row only when both sides are final. */
import React, { useMemo } from "react";
import { BandTrendBadge } from "@/components/BandTrendBadge";

// Preferred pillar column order (falls back to whatever the data provides).
const PILLAR_ORDER = [
  "Instruction", "Administrative Leadership", "Fiscal", "Governance",
  "Compliance", "Student Performance", "School Management", "CMO/EMO Management",
  "Mission and Key Design Elements", "Mission / Key Design Elements",
];

// Risk bands classify on the underlying (one-decimal) diagnostic score (80/60, Stage B).
const BANDS = [
  { min: 80, label: "Healthy (80-100)", cls: "text-[#166534]", swatch: "bg-[#16a34a]" },
  { min: 60, label: "Caution (60-79)", cls: "text-[#92400e]", swatch: "bg-[#d97706]" },
  { min: 0, label: "High Risk (0-59)", cls: "text-[#991b1b]", swatch: "bg-[#dc2626]" },
];
const BAND_CELL = {
  "Healthy (80-100)": "text-[#166534]",
  "Caution (60-79)": "text-[#92400e]",
  "High Risk (0-59)": "text-[#991b1b]",
};
const CELL_BG = { 80: "bg-[#dcfce7] text-[#166534]", 60: "bg-[#fef3c7] text-[#92400e]", 0: "bg-[#fee2e2] text-[#991b1b]" };
const bandFor = (v) => {
  if (v == null) return { label: "No score", cls: "bg-slate-100 text-slate-400" };
  const b = BANDS.find((x) => v >= x.min);
  const min = v >= 80 ? 80 : v >= 60 ? 60 : 0;
  return { label: b.label, cls: CELL_BG[min] };
};
const fmt1 = (v) => (v == null ? "—" : Number(v).toFixed(1));

const STATUS_LABEL = {
  not_started: "Not started",
  in_progress: "In progress",
  awaiting_approval: "Awaiting approval",
  final: "Final",
};

// Concise header labels (rendered over two lines) + full pillar name on hover.
const LABEL = {
  __overall__: ["Overall"],
  Instruction: ["Instruction"],
  "Administrative Leadership": ["Admin", "Leadership"],
  Fiscal: ["Fiscal"],
  Governance: ["Governance"],
  Compliance: ["Compliance"],
  "Student Performance": ["Student", "Performance"],
  "School Management": ["School", "Mgmt"],
  "CMO/EMO Management": ["School", "Mgmt"],
  "Mission and Key Design Elements": ["Mission /", "KDE"],
  "Mission / Key Design Elements": ["Mission /", "KDE"],
};
const FULL = {
  __overall__: "Overall diagnostic score",
  "Administrative Leadership": "Administrative Leadership",
  "School Management": "School Management",
  "CMO/EMO Management": "School Management",
  "Mission and Key Design Elements": "Mission and Key Design Elements",
  "Mission / Key Design Elements": "Mission and Key Design Elements",
};
const headerLines = (title) => LABEL[title] || [title];
const fullName = (title) => FULL[title] || title;

// Pull the per-perspective view for a school row.
const perspectiveOf = (s, kind) => {
  if (kind === "school") {
    return {
      label: "School Assessment",
      isFinal: !!s.assessment_is_final,
      status: s.assessment_status || (s.has_diagnostic ? "in_progress" : "not_started"),
      overall: s.assessment_is_final ? s.score : null,
      pillars: s.pillars || [],
    };
  }
  const pa = s.portfolio_assessment || {};
  return {
    label: "Portfolio Assessment",
    isFinal: !!pa.is_final,
    status: pa.status || "not_started",
    overall: pa.is_final ? pa.score : null,
    pillars: pa.pillars || [],
  };
};

const scoreByTitle = (pillars, title) => {
  const p = (pillars || []).find((x) => x.title === title);
  return p ? p.score : null;
};

export function PortfolioScoreMatrix({ schools, onSchool, onCell }) {
  const columns = useMemo(() => {
    const titles = [];
    for (const s of schools) {
      const lists = [s.pillars || [], (s.portfolio_assessment || {}).pillars || []];
      for (const list of lists) {
        for (const p of list) if (!titles.includes(p.title)) titles.push(p.title);
      }
    }
    titles.sort((a, b) => {
      const ia = PILLAR_ORDER.indexOf(a), ib = PILLAR_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
    return titles;
  }, [schools]);

  if (!schools.length) return <div className="p-8 text-center text-sm text-slate-400">No schools to display.</div>;

  const scoreCols = ["__overall__", ...columns];
  const minWidth = 300 + scoreCols.length * 60;

  const HeaderCell = ({ title }) => {
    const overall = title === "__overall__";
    return (
      <th title={fullName(title)} aria-label={fullName(title)}
        className={`px-1 py-2.5 text-center align-bottom ${overall ? "bg-slate-100 border-r-2 border-slate-300" : "bg-slate-50"}`}>
        <div className={`text-[10px] uppercase tracking-tight leading-[1.15] ${overall ? "font-bold text-slate-700" : "font-semibold text-slate-500"}`}>
          {headerLines(title).map((l, i) => <div key={i}>{l}</div>)}
        </div>
      </th>
    );
  };

  const StatusChip = ({ status }) => (
    <span className="inline-flex items-center justify-center rounded-md bg-slate-100 px-2 h-7 text-[10.5px] font-semibold text-slate-500 whitespace-nowrap">
      {STATUS_LABEL[status] || "Not started"}
    </span>
  );

  const PerspectiveRow = ({ s, kind, gap }) => {
    const v = perspectiveOf(s, kind);
    const isSchool = kind === "school";
    return (
      <tr
        className={`${isSchool ? "border-t-2 border-slate-200" : "border-t border-slate-100"} hover:bg-slate-50/40`}
        data-testid={`matrix-row-${kind}-${s.tenant_id}`}
      >
        <td className={`sticky left-0 z-10 bg-white px-3 ${isSchool ? "pt-2 pb-1" : "pt-1 pb-2"} align-top`}>
          {isSchool && (
            <button
              className="block text-left font-semibold text-slate-800 hover:text-red-700 leading-tight truncate w-full"
              title={s.school_name}
              onClick={() => onSchool && onSchool(s)}
              data-testid={`matrix-school-${s.tenant_id}`}
            >
              {s.school_name}
            </button>
          )}
          {isSchool && (
            <div className="mt-1">
              <BandTrendBadge score={s.score} prevScore={s.prev_score} showThisPeriod={false} testid={`matrix-trend-${s.tenant_id}`} />
            </div>
          )}
          <span
            className={`mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide ${isSchool ? "text-slate-500" : "text-[#007a9e] pl-2"}`}
            data-testid={`matrix-persp-${kind}-${s.tenant_id}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${isSchool ? "bg-slate-400" : "bg-[#007a9e]"}`} />
            {v.label}
          </span>
        </td>

        {scoreCols.map((title, i) => {
          const isOverall = title === "__overall__";
          const cellVal = isOverall ? v.overall : scoreByTitle(v.pillars, title);
          const band = bandFor(cellVal);
          return (
            <td key={title} className={`px-0.5 py-1.5 text-center ${isOverall ? "bg-slate-50 border-r-2 border-slate-200" : ""}`}>
              {!v.isFinal ? (
                isOverall ? <StatusChip status={v.status} /> : <span className="text-slate-300 text-xs">—</span>
              ) : (
                <div className="flex flex-col items-center gap-0.5">
                  <button
                    title={`${fullName(title)}: ${band.label}${cellVal != null ? ` · ${fmt1(cellVal)}` : ""}`}
                    onClick={() => onCell && !isOverall && onCell(s, title)}
                    className={`mx-auto inline-flex h-8 w-full min-w-[40px] max-w-[72px] items-center justify-center rounded-md text-[13px] font-bold ${band.cls}`}
                    data-testid={`matrix-cell-${kind}-${s.tenant_id}-${i}`}
                  >
                    {fmt1(cellVal)}
                  </button>
                  {isOverall && gap != null && (
                    <span
                      className={`text-[9.5px] font-bold ${gap > 0 ? "text-blue-600" : gap < 0 ? "text-amber-600" : "text-slate-400"}`}
                      title="Portfolio minus School (both final)"
                      data-testid={`matrix-gap-${s.tenant_id}`}
                    >
                      {gap > 0 ? "+" : ""}{gap} vs School
                    </span>
                  )}
                </div>
              )}
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <div data-testid="portfolio-score-matrix">
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full table-fixed border-collapse text-sm" style={{ minWidth }}>
          <colgroup>
            <col style={{ width: "28%" }} />
            {scoreCols.map((c) => <col key={c} />)}
          </colgroup>
          <thead>
            <tr className="text-left">
              <th className="sticky left-0 z-20 bg-slate-50 px-3 py-2.5 text-[10px] uppercase tracking-wide text-slate-500 align-bottom">School / Assessment</th>
              {scoreCols.map((c) => <HeaderCell key={c} title={c} />)}
            </tr>
          </thead>
          <tbody>
            {schools.map((s) => {
              const pa = s.portfolio_assessment || {};
              const gap = (s.assessment_is_final && pa.is_final && s.score != null && pa.score != null)
                ? Math.round((pa.score - s.score) * 10) / 10
                : null;
              return (
                <React.Fragment key={s.tenant_id}>
                  <PerspectiveRow s={s} kind="school" />
                  <PerspectiveRow s={s} kind="portfolio" gap={gap} />
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Mobile horizontal-scroll cue */}
      <div className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-400 sm:hidden" data-testid="matrix-scroll-cue">
        <span>Swipe horizontally to see all pillars</span><span aria-hidden>→</span>
      </div>
      {/* Legend */}
      <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="matrix-legend">
        {BANDS.map((b) => (
          <span key={b.label} className="flex items-center gap-1.5 text-[11px] text-slate-600">
            <span className={`h-3 w-3 rounded ${b.swatch}`} />{b.label}
          </span>
        ))}
        <span className="flex items-center gap-1.5 text-[11px] text-slate-600"><span className="h-3 w-3 rounded bg-slate-100 border border-slate-200" />Status shown until an assessment is final</span>
        <span className="flex items-center gap-1.5 text-[11px] text-slate-600"><span className="h-1.5 w-1.5 rounded-full bg-slate-400" />School</span>
        <span className="flex items-center gap-1.5 text-[11px] text-slate-600"><span className="h-1.5 w-1.5 rounded-full bg-[#007a9e]" />Portfolio</span>
      </div>
    </div>
  );
}
